import { useState, useRef, useEffect } from 'react'
import { Button, Input } from '@talktoast/ui'
import { Toasty } from '@talktoast/shared'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

export interface ChatBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  currentScreen?: string
  messages?: Message[]
  onSendMessage?: (message: string) => Promise<void>
}

export default function ChatBottomSheet({
  isOpen,
  onClose,
  currentScreen = 'home',
  messages = [],
  onSendMessage
}: ChatBottomSheetProps) {
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [sheetHeight, setSheetHeight] = useState<'collapsed' | 'half' | 'full'>('half')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const startYRef = useRef<number>(0)
  const currentHeightRef = useRef<number>(0)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleSend = async () => {
    if (!input.trim() || !onSendMessage) return

    setIsSending(true)
    try {
      await onSendMessage(input.trim())
      setInput('')
    } catch (error) {
      console.error('[ChatBottomSheet] Error sending message:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const getHeightClass = () => {
    switch (sheetHeight) {
      case 'collapsed':
        return 'h-16'
      case 'half':
        return 'h-[50vh]'
      case 'full':
        return 'h-[90vh]'
      default:
        return 'h-[50vh]'
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    startYRef.current = e.touches[0].clientY
    currentHeightRef.current = sheetRef.current?.offsetHeight || 0
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY
    const diff = startYRef.current - currentY

    // Dragging down to close
    if (diff < -50) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-50 transition-all duration-300 ${getHeightClass()}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <div>
            <h3 className="font-semibold text-gray-900">TalkToast Assistant</h3>
            <p className="text-xs text-gray-500">
              {currentScreen === 'calendar' && 'Help with your meal calendar'}
              {currentScreen === 'shopping' && 'Shopping list assistance'}
              {currentScreen === 'history' && 'Recipe history questions'}
              {currentScreen === 'home' && 'Ask me anything about meal planning'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setSheetHeight((prev) =>
                  prev === 'half' ? 'full' : prev === 'full' ? 'collapsed' : 'half'
                )
              }
              className="p-2 text-gray-500 hover:text-gray-700"
              title="Resize"
            >
              {sheetHeight === 'collapsed' && '⬆️'}
              {sheetHeight === 'half' && '⬆️'}
              {sheetHeight === 'full' && '⬇️'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* "Still Toasting" Message */}
        <div className="flex-1 overflow-y-auto flex items-center justify-center p-8">
          <div className="text-center max-w-sm">
            <Toasty size="xl" className="mx-auto mb-4" alt="Feature coming soon" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              This Feature is Still Toasting!
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              We're perfecting the TalkToast Assistant to help you with:
            </p>
            <ul className="text-sm text-gray-700 space-y-2 text-left mb-6">
              <li>• Recipe questions and substitutions</li>
              <li>• Meal planning advice</li>
              <li>• Shopping list help</li>
              <li>• Calendar scheduling tips</li>
            </ul>
            <div className="flex items-center justify-center gap-2 text-toast-gold">
              <span className="text-2xl">⏳</span>
              <p className="text-sm font-medium">Check back soon – it'll be golden!</p>
            </div>
          </div>
        </div>

        {/* Input - Disabled "Coming Soon" State */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex gap-2 opacity-50">
            <Input
              value=""
              onChange={() => {}}
              placeholder="Coming soon..."
              disabled={true}
              className="flex-1"
            />
            <Button
              onClick={() => {}}
              disabled={true}
              variant="primary"
            >
              📤
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
